import { Component, OnInit } from '@angular/core';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import moment from 'moment-timezone';
import { finalize } from 'rxjs';
import { STATUS_CODES } from 'src/app/constants';
import {
  DeviceService,
  DownloadService,
  ToasterService,
} from 'src/app/services';

export type DeviceLogQuickPick = '24h' | '7d' | '30d';

const MAX_RANGE_DAYS = 31;
const NO_LOGS_MESSAGE = 'No logs found for the selected period';
const RANGE_TOO_LARGE_MESSAGE =
  'Date range cannot exceed 31 days. Please select a shorter period.';

@Component({
  selector: 'app-download-device-logs-modal',
  templateUrl: './download-device-logs-modal.component.html',
  styleUrls: ['./download-device-logs-modal.component.scss'],
  standalone: false,
})
export class DownloadDeviceLogsModalComponent implements OnInit {
  title = 'Download Device Logs';
  deviceId!: string;
  fileName!: string;

  startDateTime = '';
  endDateTime = '';
  activeQuickPick: DeviceLogQuickPick | null = '24h';
  rangeError: string | null = null;
  emptyMessage: string | null = null;
  loading = false;

  readonly quickPicks: { key: DeviceLogQuickPick; label: string }[] = [
    { key: '24h', label: 'Last 24h' },
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
  ];

  constructor(
    public modalRef: MdbModalRef<DownloadDeviceLogsModalComponent>,
    private deviceService: DeviceService,
    private downloadService: DownloadService,
    private toasterService: ToasterService,
  ) {}

  ngOnInit(): void {
    this.applyQuickPick('24h');
  }

  applyQuickPick(pick: DeviceLogQuickPick): void {
    const end = moment();
    let start = moment();
    switch (pick) {
      case '24h':
        start = moment().subtract(24, 'hours');
        break;
      case '7d':
        start = moment().subtract(7, 'days');
        break;
      case '30d':
        start = moment().subtract(30, 'days');
        break;
    }
    this.startDateTime = this.toDatetimeLocalValue(start);
    this.endDateTime = this.toDatetimeLocalValue(end);
    this.activeQuickPick = pick;
    this.rangeError = null;
    this.emptyMessage = null;
  }

  onRangeInputChange(): void {
    this.activeQuickPick = null;
    this.emptyMessage = null;
    this.validateRange();
  }

  download(): void {
    if (this.loading || !this.deviceId) {
      return;
    }
    if (!this.validateRange()) {
      return;
    }

    this.emptyMessage = null;
    this.loading = true;

    const startDate = moment(this.startDateTime).toISOString();
    const endDate = moment(this.endDateTime).toISOString();

    this.deviceService
      .getWatchStatistics(this.deviceId, { startDate, endDate })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (response) => {
          if (response.statusCode != STATUS_CODES.OK) {
            return;
          }
          if (!this.hasLogs(response.data)) {
            this.emptyMessage = NO_LOGS_MESSAGE;
            this.toasterService.show('Info', NO_LOGS_MESSAGE);
            return;
          }
          this.downloadService.downloadJson(
            response.data,
            this.fileName || this.deviceId,
          );
          this.modalRef.close(true);
        },
        error: (error) => {
          console.log('error getting watch statistics', error);
          this.toasterService.showError(error);
        },
      });
  }

  close(): void {
    if (this.loading) {
      return;
    }
    this.modalRef.close();
  }

  private validateRange(): boolean {
    if (!this.startDateTime || !this.endDateTime) {
      this.rangeError = 'Start and end date/time are required.';
      return false;
    }

    const start = moment(this.startDateTime);
    const end = moment(this.endDateTime);

    if (!start.isValid() || !end.isValid()) {
      this.rangeError = 'Please enter a valid start and end date/time.';
      return false;
    }

    if (end.isBefore(start)) {
      this.rangeError = 'End date/time must be after start date/time.';
      return false;
    }

    const rangeDays = end.diff(start, 'days', true);
    if (rangeDays > MAX_RANGE_DAYS) {
      this.rangeError = RANGE_TOO_LARGE_MESSAGE;
      return false;
    }

    this.rangeError = null;
    return true;
  }

  private hasLogs(data: unknown): boolean {
    if (data == null) {
      return false;
    }
    if (Array.isArray(data)) {
      return data.length > 0;
    }
    if (typeof data === 'string') {
      return data.trim().length > 0;
    }
    if (typeof data === 'object') {
      const record = data as Record<string, unknown>;
      const keys = Object.keys(record);
      if (keys.length === 0) {
        return false;
      }
      for (const nestedKey of ['rows', 'logs', 'items', 'data']) {
        if (nestedKey in record) {
          return this.hasLogs(record[nestedKey]);
        }
      }
      return true;
    }
    return true;
  }

  private toDatetimeLocalValue(value: moment.Moment): string {
    return value.format('YYYY-MM-DDTHH:mm');
  }
}
